import React from 'react';
import { Bot, Eye, Shield } from 'lucide-react';
import type { DaemonRecord } from '../../../generated/api/types.gen.js';
import { Badge } from '../../../components/ui/index.js';
import type { BadgeVariant } from '../../../components/ui/index.js';
import { humanize } from '../admin-format.js';

const TYPE_ICONS: Record<string, React.ReactNode> = {
  guardian: <Shield size={12} />,
  sentinel: <Eye size={12} />,
  operative: <Bot size={12} />,
};

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  idle: 'neutral',
  running: 'success',
  paused: 'warning',
  error: 'danger',
  disabled: 'neutral',
};

interface DaemonStatusBarProps {
  daemon: DaemonRecord;
}

/** The status badges beside a daemon's title: type, run status, department, permission tier and enabled state. */
export const DaemonStatusBar: React.FC<DaemonStatusBarProps> = ({ daemon }) => {
  const onProbation = daemon.probationUntil && new Date(daemon.probationUntil) > new Date();

  return (
    <>
      <Badge variant="neutral" icon={(daemon.type ? TYPE_ICONS[daemon.type] : undefined) ?? <Bot size={12} />}>
        {humanize(daemon.type ?? 'operative')}
      </Badge>
      <Badge variant={(daemon.status ? STATUS_VARIANTS[daemon.status] : undefined) ?? 'neutral'} dot>
        {humanize(daemon.status ?? 'unknown')}
      </Badge>
      <Badge variant="neutral">{humanize(daemon.department ?? 'custom')}</Badge>
      <Badge variant="info">{humanize(daemon.permissionTier ?? 'observer')}</Badge>
      <Badge variant={daemon.enabled ? 'success' : 'neutral'}>{daemon.enabled ? 'Enabled' : 'Disabled'}</Badge>
      {onProbation && <Badge variant="warning">On probation</Badge>}
    </>
  );
};
